import React, { FC, useRef, useState } from 'react';
import api from '../../api';

export interface PromotionCardMedia {
  url: string;
  kind: 'image' | 'video';
  contentType: 'image/jpeg' | 'image/png' | 'video/mp4';
  bytes: number;
}

export interface PromotionCardCopy {
  body: string;
  footer: string | null;
  button: string;
  /** Translations only: this language's own media; absent uses the English media. */
  media?: PromotionCardMedia | null;
}

export interface PromotionCard {
  media: PromotionCardMedia | null;
  body: string;
  footer: string | null;
  button: string;
  translations: Partial<Record<'fr' | 'de' | 'es' | 'it', PromotionCardCopy>>;
}

type Approval = {
  language: 'en' | 'fr' | 'de' | 'es' | 'it';
  status: 'not_submitted' | 'pending' | 'approved' | 'rejected';
  rejectionReason: string | null;
};

type Warning = { code: string; message: string };

type CardLanguage = 'fr' | 'de' | 'es' | 'it';
type PromotionCardLanguage = Approval['language'];
type LanguageTabState =
  | 'done'
  | 'not_done'
  | 'pending'
  | 'approved'
  | 'rejected';

const CARD_LANGUAGES: PromotionCardLanguage[] = ['en', 'fr', 'de', 'es', 'it'];
const LANGUAGE_NAMES: Record<PromotionCardLanguage, string> = {
  en: 'English',
  fr: 'French',
  de: 'German',
  es: 'Spanish',
  it: 'Italian',
};
const LANGUAGE_TAB_STATUS: Record<
  LanguageTabState,
  { icon: string; label: string }
> = {
  done: { icon: '✓', label: 'Done' },
  not_done: { icon: '●', label: 'Not done' },
  pending: { icon: '◷', label: 'Waiting for approval' },
  approved: { icon: '✓', label: 'Approved' },
  rejected: { icon: '✕', label: 'Rejected' },
};
const VAGUE_BUTTONS = new Set([
  'ok',
  'yes',
  'click here',
  'learn more',
  'more',
]);

const blankCard = (): PromotionCard => ({
  media: null,
  body: '',
  footer: null,
  button: '',
  translations: {},
});

function warnings(copy: PromotionCardCopy, hasMedia: boolean): Warning[] {
  const result: Warning[] = [];
  if (copy.body.length > 1024) {
    result.push({
      code: 'over_limit_body',
      message: 'Card body exceeds 1024 characters.',
    });
  }
  if (copy.footer !== null && copy.footer.length > 60) {
    result.push({
      code: 'over_limit_footer',
      message: 'Card footer exceeds 60 characters.',
    });
  }
  if (copy.button.length > 20) {
    result.push({
      code: 'over_limit_button',
      message: 'Card button exceeds 20 characters.',
    });
  }
  const estimatedLines = copy.body
    .split('\n')
    .reduce(
      (total, paragraph) =>
        total + Math.max(1, Math.ceil(paragraph.length / 38)),
      0,
    );
  if (hasMedia && estimatedLines > 3) {
    result.push({
      code: 'fold',
      message:
        "WhatsApp will fold this after about 3 lines; the rest is behind 'Read more'.",
    });
  }
  if (VAGUE_BUTTONS.has(copy.button.trim().toLowerCase())) {
    result.push({
      code: 'button_vague',
      message: "Say what the tap does, e.g. 'Try groceries'.",
    });
  }
  if (/https?:\/\//i.test(copy.body)) {
    result.push({
      code: 'link_in_body',
      message:
        "Links in the card aren't tracked; the secretary sends the tracked link after a tap.",
    });
  }
  return result;
}

const sizeMb = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const PromotionCardEditor: FC<{
  card: PromotionCard | null;
  onChange: (card: PromotionCard | null) => void;
  slug: string | null;
  editable: boolean;
  locked: boolean;
  canSubmit: boolean;
  hasLink?: boolean;
  cardOnly?: boolean;
  approvals: Approval[];
  auth: () => Promise<{ headers: { Authorization: string } }>;
  onSubmitted: () => Promise<void>;
}> = ({
  card,
  onChange,
  slug,
  editable,
  locked,
  canSubmit,
  hasLink = false,
  cardOnly = false,
  approvals,
  auth,
  onSubmitted,
}) => {
  const buttonHint = hasLink
    ? 'Opens their link'
    : 'Replies to their secretary';
  const [busy, setBusy] = useState<'media' | 'translations' | 'submit' | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [languageErrors, setLanguageErrors] = useState<Record<string, string>>(
    {},
  );
  const [filenames, setFilenames] = useState<
    Partial<Record<PromotionCardLanguage, string>>
  >({});
  const cardEditable = editable && !locked;
  const [activeLanguage, setActiveLanguage] =
    useState<PromotionCardLanguage>('en');

  const ownMedia = (language: PromotionCardLanguage) =>
    !card
      ? null
      : language === 'en'
        ? card.media
        : (card.translations[language]?.media ?? null);

  const mediaFor = (language: PromotionCardLanguage) =>
    ownMedia(language) ?? card?.media ?? null;

  // An upload can finish after copy edits; apply its media to the latest card.
  const latestCard = useRef(card);
  latestCard.current = card;

  const setLanguageMedia = (
    language: PromotionCardLanguage,
    media: PromotionCardMedia | null,
  ) => {
    const card = latestCard.current;
    if (!card) return;
    if (language === 'en') {
      onChange({ ...card, media });
      return;
    }
    const translation = card.translations[language];
    if (!translation) return;
    const copy: PromotionCardCopy = { ...translation };
    delete copy.media;
    onChange({
      ...card,
      translations: {
        ...card.translations,
        [language]: media ? { ...copy, media } : copy,
      },
    });
  };

  const uploadMedia = async (language: PromotionCardLanguage, file?: File) => {
    if (!file || !card) return;
    const supported = ['image/jpeg', 'image/png', 'video/mp4'].includes(
      file.type,
    );
    if (!supported) {
      setError('Choose a JPEG, PNG or MP4 file.');
      return;
    }
    const maxBytes = file.type === 'video/mp4' ? 16 : 5;
    if (file.size === 0 || file.size > maxBytes * 1024 * 1024) {
      setError(`This file must be between 1 byte and ${maxBytes} MB.`);
      return;
    }
    setBusy('media');
    setError(null);
    try {
      const data = new FormData();
      data.append('file', file);
      const result = await api.post<{
        url: string;
        kind: 'image' | 'video';
        contentType: 'image/jpeg' | 'image/png' | 'video/mp4';
        bytes: number;
      }>('/promotion-stats/promotions/card-media', data, await auth());
      const { url, kind, contentType, bytes } = result.data;
      setLanguageMedia(language, { url, kind, contentType, bytes });
      setFilenames((current) => ({ ...current, [language]: file.name }));
    } catch {
      setError('Could not upload this media file.');
    } finally {
      setBusy(null);
    }
  };

  const draftTranslations = async () => {
    if (!slug || !card) return;
    setBusy('translations');
    setError(null);
    try {
      const result = await api.post<{
        translations: Partial<Record<CardLanguage, PromotionCardCopy>>;
      }>(
        `/promotion-stats/promotions/${encodeURIComponent(slug)}/card-translations`,
        {},
        await auth(),
      );
      const drafted = Object.fromEntries(
        Object.entries(result.data.translations).map(([language, copy]) => {
          const media = card.translations[language as CardLanguage]?.media;
          return [language, media ? { ...copy, media } : copy];
        }),
      );
      onChange({
        ...card,
        translations: { ...card.translations, ...drafted },
      });
    } catch {
      setError('Could not draft translations.');
    } finally {
      setBusy(null);
    }
  };

  const submit = async () => {
    if (!slug || !canSubmit) return;
    setBusy('submit');
    setError(null);
    setLanguageErrors({});
    try {
      const result = await api.post<{ errors?: Record<string, string> }>(
        `/promotion-stats/promotions/${encodeURIComponent(slug)}/card-submit`,
        {},
        await auth(),
      );
      setLanguageErrors(result.data.errors ?? {});
      await onSubmitted();
    } catch {
      setError('Could not submit the card for approval.');
    } finally {
      setBusy(null);
    }
  };

  const approvalFor = (language: Approval['language']) =>
    approvals.find((approval) => approval.language === language) ?? {
      language,
      status: 'not_submitted' as const,
      rejectionReason: null,
    };

  const copyForLanguage = (
    language: PromotionCardLanguage,
  ): PromotionCardCopy => {
    if (!card) return { body: '', footer: null, button: '' };
    if (language === 'en') {
      return { body: card.body, footer: card.footer, button: card.button };
    }
    return (
      card.translations[language] ?? {
        body: '',
        footer: null,
        button: '',
      }
    );
  };

  const hasTranslation = (language: PromotionCardLanguage) =>
    language !== 'en' && Boolean(card?.translations[language]);

  const updateLanguageCopy = (
    language: PromotionCardLanguage,
    copy: PromotionCardCopy,
  ) => {
    if (!card) return;
    if (language === 'en') {
      onChange({ ...card, ...copy });
      return;
    }
    onChange({
      ...card,
      translations: { ...card.translations, [language]: copy },
    });
  };

  const languageTabState = (
    language: PromotionCardLanguage,
  ): LanguageTabState => {
    const approval = approvalFor(language);
    if (approval.status !== 'not_submitted') return approval.status;

    const copy = copyForLanguage(language);
    const hasCopy = language === 'en' || hasTranslation(language);
    const hasOverLimitWarning = warnings(
      copy,
      Boolean(mediaFor(language)),
    ).some((warning) => warning.code.startsWith('over_limit_'));

    return hasCopy &&
      copy.body.trim().length > 0 &&
      copy.button.trim().length > 0 &&
      !hasOverLimitWarning
      ? 'done'
      : 'not_done';
  };

  const renderMediaField = (language: PromotionCardLanguage) => {
    if (!card) return null;
    const media = ownMedia(language);
    const label =
      language === 'en'
        ? 'Media, optional'
        : `Media for ${LANGUAGE_NAMES[language]}, optional`;
    return (
      <>
        <label className='promotion-card-upload'>
          {label} (JPEG/PNG up to 5 MB, MP4 up to 16 MB)
          <input
            type='file'
            accept='image/jpeg,image/png,video/mp4'
            disabled={!cardEditable || busy !== null}
            onChange={(event) => {
              void uploadMedia(language, event.target.files?.[0]);
              event.target.value = '';
            }}
          />
        </label>
        {!media && language !== 'en' && card.media && (
          <p className='launch-hint'>Uses the English media</p>
        )}
        {media && (
          <div className='promotion-card-media-preview'>
            {media.kind === 'image' ? (
              <img
                src={media.url}
                alt={`WhatsApp card media, ${LANGUAGE_NAMES[language]}`}
              />
            ) : (
              <video src={media.url} muted controls />
            )}
            <span>
              {filenames[language] ?? media.url.split('/').pop()} ·{' '}
              {sizeMb(media.bytes)}
            </span>
            {cardEditable && (
              <button
                type='button'
                className='launch-btn launch-btn--quiet'
                disabled={busy !== null}
                onClick={() => {
                  setLanguageMedia(language, null);
                  setFilenames((current) => {
                    const next = { ...current };
                    delete next[language];
                    return next;
                  });
                }}
              >
                Remove
              </button>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className='launch-card promotion-card-editor'>
      <div className='launch-card-header'>
        <h4>WhatsApp card</h4>
      </div>
      <label className='promotion-card-toggle'>
        <input
          type='checkbox'
          checked={Boolean(card)}
          disabled={!editable || locked}
          onChange={(event) =>
            onChange(event.target.checked ? blankCard() : null)
          }
        />
        {cardOnly
          ? 'Send a WhatsApp card to everyone'
          : 'Send a WhatsApp card to people outside the 24-hour window'}
      </label>
      {card && (
        <>
          <div
            className='promotion-tabs promotion-card-language-tabs'
            role='tablist'
            aria-label='WhatsApp card languages'
          >
            {CARD_LANGUAGES.map((language) => {
              const status = languageTabState(language);
              const statusPresentation = LANGUAGE_TAB_STATUS[status];
              const tabId = `promotion-card-language-tab-${language}`;
              const panelId = `promotion-card-language-panel-${language}`;
              return (
                <button
                  key={language}
                  id={tabId}
                  type='button'
                  className={`promotion-tab ${
                    activeLanguage === language ? 'is-active' : ''
                  }`}
                  role='tab'
                  aria-selected={activeLanguage === language}
                  aria-controls={panelId}
                  onClick={() => setActiveLanguage(language)}
                >
                  {LANGUAGE_NAMES[language]}
                  <span
                    className={`promotion-card-language-status promotion-card-language-status--${status}`}
                    role='img'
                    aria-label={statusPresentation.label}
                  >
                    {statusPresentation.icon}
                  </span>
                </button>
              );
            })}
          </div>
          {CARD_LANGUAGES.map((language) => {
            const copy = copyForLanguage(language);
            const translation =
              language === 'en' ? null : (card.translations[language] ?? null);
            const approval = approvalFor(language);
            const isActive = activeLanguage === language;
            const warningsForLanguage = warnings(
              copy,
              Boolean(mediaFor(language)),
            );
            const approvalMessage =
              approval.status === 'pending'
                ? 'Waiting for WhatsApp approval'
                : approval.status === 'approved'
                  ? 'Approved by WhatsApp'
                  : approval.status === 'rejected'
                    ? `Rejected${
                        approval.rejectionReason
                          ? `: ${approval.rejectionReason}`
                          : ''
                      }`
                    : null;

            return (
              <div
                key={language}
                id={`promotion-card-language-panel-${language}`}
                className='promotion-card-language-panel'
                role='tabpanel'
                aria-labelledby={`promotion-card-language-tab-${language}`}
                tabIndex={isActive ? 0 : -1}
                hidden={!isActive}
              >
                {language !== 'en' && !translation && (
                  <>
                    <p className='promotion-card-language-empty'>
                      No {LANGUAGE_NAMES[language]} version yet.
                    </p>
                    <div className='promotion-card-translation-actions'>
                      <button
                        type='button'
                        className='launch-btn launch-btn--quiet'
                        disabled={!cardEditable || !canSubmit || busy !== null}
                        onClick={() => void draftTranslations()}
                      >
                        {busy === 'translations'
                          ? 'Drafting…'
                          : 'Draft translations'}
                      </button>
                      {!canSubmit && editable && (
                        <span className='launch-hint'>
                          Save the card before submitting.
                        </span>
                      )}
                    </div>
                  </>
                )}
                {(language === 'en' || translation) &&
                  renderMediaField(language)}
                <label>
                  Body
                  <textarea
                    rows={language === 'en' ? 4 : 3}
                    maxLength={1024}
                    value={copy.body}
                    disabled={!cardEditable}
                    onChange={(event) =>
                      updateLanguageCopy(language, {
                        ...copy,
                        body: event.target.value,
                      })
                    }
                  />
                  <span className='promotion-card-counter'>
                    {copy.body.length} / 1024
                  </span>
                </label>
                <label>
                  Footer (optional)
                  <input
                    maxLength={60}
                    value={copy.footer ?? ''}
                    disabled={!cardEditable}
                    onChange={(event) =>
                      updateLanguageCopy(language, {
                        ...copy,
                        footer: event.target.value || null,
                      })
                    }
                  />
                  <span className='promotion-card-counter'>
                    {copy.footer?.length ?? 0} / 60
                  </span>
                </label>
                <label>
                  Button
                  <input
                    maxLength={20}
                    value={copy.button}
                    disabled={!cardEditable}
                    onChange={(event) =>
                      updateLanguageCopy(language, {
                        ...copy,
                        button: event.target.value,
                      })
                    }
                  />
                  <span className='promotion-card-counter'>
                    {copy.button.length} / 20
                  </span>
                  <span className='launch-hint'>{buttonHint}</span>
                </label>
                {warningsForLanguage.length > 0 && (
                  <ul className='promotion-card-warnings'>
                    {warningsForLanguage.map((warning) => (
                      <li key={warning.code}>{warning.message}</li>
                    ))}
                  </ul>
                )}
                {translation && cardEditable && (
                  <button
                    type='button'
                    className='launch-btn launch-btn--quiet'
                    onClick={() => {
                      const translations = { ...card.translations };
                      delete translations[language as CardLanguage];
                      onChange({ ...card, translations });
                    }}
                  >
                    Remove {LANGUAGE_NAMES[language]} version
                  </button>
                )}
                {approvalMessage && (
                  <p
                    className={`promotion-card-language-message promotion-card-language-message--${approval.status}`}
                  >
                    {approvalMessage}
                  </p>
                )}
                {languageErrors[language] && (
                  <p className='promotion-card-error'>
                    {languageErrors[language]}
                  </p>
                )}
              </div>
            );
          })}
          {locked && (
            <p className='launch-hint'>Card copy is locked after submission</p>
          )}
          {canSubmit && (
            <button
              className='launch-btn launch-btn--quiet'
              disabled={busy !== null}
              onClick={submit}
            >
              {busy === 'submit'
                ? 'Submitting…'
                : 'Submit for WhatsApp approval'}
            </button>
          )}
          {error && <p className='promotion-card-error'>{error}</p>}
        </>
      )}
    </div>
  );
};

export default PromotionCardEditor;
